// Deutsche Datums-/Zeit-EINGABE — die EINE Implementierung fuer alle Seiten.
//
// WARUM ueberhaupt eigene Felder: `<input type="date">`, `type="datetime-local">`
// und `type="time">` rendern ZWINGEND im Format der UI-Sprache des BROWSERS und
// lassen sich per CSS/Attribut nicht umformatieren. Bei einem englischen Chrome
// steht dort `09/03/2026` bzw. `02:30 PM` — nicht TT.MM.JJJJ. Deshalb: normales
// Textfeld + Auswahllisten, die in JEDEM Browser identisch aussehen.
//
// MESS-GOTCHA (kostet sonst Stunden): Playwrights
// `browser.newContext({ locale: "en-US" })` aendert das Anzeigeformat dieser
// nativen Felder NICHT — es setzt nur `navigator.language`/`Intl`. Das Format der
// nativen Controls haengt an der UI-Sprache des Browser-PROZESSES, also an
// `chromium.launch({ args: ["--lang=en-US"] })`. Ein Test, der nur `locale`
// setzt, ist auf einem deutschen Windows GRUEN, obwohl der Nutzer MM/DD/YYYY
// sieht. Beweis-Walks muessen BEIDE Schalter setzen (siehe
// frontend/deutsches-datum-walk.mjs).
//
// Das Modul ist bewusst DOM-frei und ohne React-Import: reine String-/Date-
// Mathematik, damit es in Node (Dry-Run) ohne Browser testbar ist.

/** Date → „TT.MM.JJJJ". */
export function fmtDeDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** Date → „HH:MM" (24 h, immer deutsch — unabhaengig vom Browser-Locale). */
export function fmtHm(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** „HH:MM" → Minuten seit 00:00 (unlesbar → 0). */
export function minutesFromHm(v: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v.trim());
  if (!m) return 0;
  return Math.min(1439, Math.max(0, +m[1] * 60 + +m[2]));
}

/** Zweistelliges Jahr → das Jahrhundert, das dem Bezugsjahr am NAECHSTEN liegt
 *  (gleitendes Fenster wie Windows/Outlook): mit ref 2026 wird „30" zu 2030,
 *  „99" aber zu 1999 — nicht 2099. Festes `2000 + yy` lag hier daneben. */
export function expandYear(yy: number, ref: Date): number {
  const refYear = ref.getFullYear();
  const base = Math.floor(refYear / 100) * 100;
  return [base - 100 + yy, base + yy, base + 100 + yy].reduce((best, c) =>
    Math.abs(c - refYear) < Math.abs(best - refYear) ? c : best
  );
}

/**
 * „TT.MM.JJJJ" → lokale Mitternacht. Tolerant bei der EINGABE (1.2.2026,
 * 01.02.26, 1.2. → Jahr aus `ref`, „12. 3. 2026" aus einem Copy-Paste), aber
 * streng bei der Gueltigkeit: der 30.02. ist `null` (kein stilles Weiterrollen
 * auf den 02.03.). Unlesbar → `null`.
 *
 * Bewusst NICHT tolerant gegenueber der ISO-Form („2026-03-12"): die Felder
 * fuehren TT.MM.JJJJ, ein ISO-Wert ist dort eine Fehleingabe und bekommt eine
 * sichtbare Meldung — statt still als 2026. Tag irgendeines Monats zu landen.
 */
export function parseDeDate(v: string, ref: Date): Date | null {
  // `\s*` an den Trennern: „12. 3. 2026" aus einem Copy-Paste ist dieselbe
  // Angabe wie „12.3.2026" — der Wert ist danach korrekt, nicht geraten.
  const m = /^(\d{1,2})\s*\.\s*(\d{1,2})\s*\.?\s*(\d{2}|\d{4})?$/.exec(v.trim());
  if (!m) return null;
  const day = +m[1];
  const month = +m[2];
  const year = m[3]
    ? m[3].length === 2
      ? expandYear(+m[3], ref)
      : +m[3]
    : ref.getFullYear();
  const d = new Date(year, month - 1, day);
  d.setHours(0, 0, 0, 0);
  // Rollover erkennen (31.04. → 01.05.) und ablehnen.
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day)
    return null;
  return d;
}

/** Uhrzeiten im Raster-Takt (SNAP_MIN = 15) → jeder Raster-Klick trifft einen Eintrag. */
export const TIME_OPTIONS: string[] = Array.from({ length: 96 }, (_, i) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(Math.floor((i * 15) / 60))}:${p((i * 15) % 60)}`;
});

/** Uebliche Termin-Laengen (Outlook-Ladder). Krumme Werte aus einem Zieh-Slot
 *  werden zur Laufzeit als Zusatz-Eintrag ergaenzt (Muster ReminderOptions). */
export const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120, 180, 240, 480];

export function durationLabel(min: number): string {
  if (min < 60) return `${min} Min.`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (m === 0) return `${h} Std.`;
  if (m === 30) return `${h},5 Std.`;
  return `${h} Std. ${m} Min.`;
}

/** Auswahlliste, die den Ist-Wert GARANTIERT enthaelt: ein krummer Wert (10:07
 *  aus einem Zieh-Slot, 25 Min. aus einem Graph-Termin) wird als Zusatz-Eintrag
 *  ergaenzt, damit das <select> nie einen FALSCHEN Wert anzeigt. */
export function withCurrent<T extends string | number>(list: T[], cur: T): T[] {
  if (list.includes(cur)) return list;
  return [...list, cur].sort((a, b) =>
    typeof a === "number" && typeof b === "number"
      ? a - b
      : String(a).localeCompare(String(b))
  );
}

// ── ISO-Bruecke (Kalendertag) ────────────────────────────────────────────────
// Fuer Filter/Endpoints, die einen KALENDERTAG als „YYYY-MM-DD" erwarten
// (`/mail/search?since=&until=`). Bewusst aus den LOKALEN Feldern gebaut, nicht
// via `toISOString()`: letzteres rechnet nach UTC und liefert in Berlin fuer
// jeden Zeitpunkt vor 01:00/02:00 Ortszeit den VORTAG.

/** Date → „YYYY-MM-DD" (lokaler Kalendertag). */
export function fmtIsoDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** „TT.MM.JJJJ" → „YYYY-MM-DD"; unlesbar/leer → `null`. */
export function deDateToIso(v: string, ref: Date): string | null {
  const d = parseDeDate(v, ref);
  return d ? fmtIsoDate(d) : null;
}

/** „YYYY-MM-DD" → „TT.MM.JJJJ"; unlesbar/leer → `""`. */
export function isoToDeDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((iso || "").trim());
  return m ? `${m[3]}.${m[2]}.${m[1]}` : "";
}

// ── Lokal-naive Zeitpunkte („YYYY-MM-DDTHH:MM") ──────────────────────────────
// Die Form, die ein `datetime-local`-Input trug und die manche Endpoints
// weiterhin erwarten (Abwesenheitsnotiz: `new Date(v).toISOString()`). Die
// Anzeige wird in deutsches Datum + Uhrzeit zerlegt, der Egress-Wert bleibt
// unverändert diese Form — die Umstellung ist rein visuell.

/** „YYYY-MM-DDTHH:MM…" → `{ date: „TT.MM.JJJJ", time: „HH:MM" }`.
 *  Unlesbar/leer → beide Felder `""` (der Aufrufer behält dann seinen Default). */
export function splitLocalNaive(v: string): { date: string; time: string } {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec((v || "").trim());
  return m ? { date: isoToDeDate(m[1]), time: m[2] } : { date: "", time: "" };
}

/** `„TT.MM.JJJJ" + „HH:MM"` → „YYYY-MM-DDTHH:MM"; unlesbar → `""`
 *  (der Aufrufer sendet dann `null` statt eines geratenen Zeitpunkts). */
export function joinLocalNaive(dateDe: string, time: string, ref: Date): string {
  const iso = deDateToIso(dateDe, ref);
  if (!iso) return "";
  const t = /^(\d{1,2}):(\d{2})$/.exec((time || "").trim());
  if (!t) return "";
  return `${iso}T${String(+t[1]).padStart(2, "0")}:${t[2]}`;
}
